import { useEffect, useState } from 'react';
import { api } from '../api';
import { useApi } from '../hooks';
import { lastRun, type IngestionStatus } from './SlackChannels';
import { ErrorState, LoadingState } from './Ui';

export function GoogleConnect({ authorized }: { authorized: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function connect(extra: '' | 'gmail' | 'calendar' | 'business' = '') {
    setBusy(true); setError('');
    try {
      const { auth_url } = await api<{ auth_url: string }>(`/api/drive/oauth/init${extra ? `?${extra}=1` : ''}`);
      const url = new URL(auth_url);
      if (url.origin !== 'https://accounts.google.com') throw new Error('Unexpected Google authorization URL.');
      window.location.assign(url.href);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not start authorization.'); setBusy(false); }
  }
  return <section className="integration-setup">
    <h3>Authorize your Google account</h3>
    <p>Save the OAuth application credentials, then sign in with the Google account that has access to your Ads manager. Grant both Drive and Google Ads permissions. Read-only Search Console access is also asked for; it is optional and feeds each client's Keywords page.</p>
    <p>In Google Cloud, add this exact authorized redirect URL:</p>
    <code className="integration-callback">{window.location.origin}/api/drive/oauth/callback</code>
    {error && <p role="alert" className="integration-notice danger">{error}</p>}
    <button className="button secondary" disabled={busy} onClick={() => void connect()}>{busy ? 'Opening Google…' : authorized ? 'Reconnect Google' : 'Connect Google'}</button>
    {authorized && <SearchConsoleNote />}
    {authorized && <GmailConnect busy={busy} onAllow={() => void connect('gmail')} />}
    {authorized && <CalendarConnect busy={busy} onAllow={() => void connect('calendar')} />}
    {authorized && <BusinessProfileConnect busy={busy} onAllow={() => void connect('business')} />}
  </section>;
}

function BusinessProfileConnect({ busy, onAllow }: { busy: boolean; onAllow: () => void }) {
  const status = useApi<{ ready: boolean }>('/api/google-business/status');
  return <div className="gmail-connect">
    <h4>Google Business Profile</h4>
    <p>Publish posts and review replies from NOLA. An admin links each Google profile to the correct client location in Reputation.</p>
    {status.data?.ready ? <p>Business Profile is allowed. Open a client’s Reputation page to link its locations.</p> : <>
      <p>Enable the Business Profile APIs for this Google Cloud project and add <code>business.manage</code> to its OAuth consent screen. Google must approve the project for Business Profile API access.</p>
      <button className="button secondary" disabled={busy} onClick={onAllow}>Allow Business Profile</button>
    </>}
    {status.error && <p role="alert" className="integration-notice danger">{status.error}</p>}
  </div>;
}

type EmailDomain = {
  domain: string; status: string; note?: string; region?: string | null; from_now: string; from_wanted: string;
  records: { type: string; name: string; value: string; priority: number | null; status: string | null }[];
};
const DOMAIN_STATUS: Record<string, string> = {
  verified: "Verified. Emails come from this address now.",
  pending: "Waiting for the DNS records. Resend checks them for up to 72 hours.",
  not_started: "Add the DNS records below, then press Check DNS now.",
  failed: "Resend could not find the records. Check them below, then press Check DNS now.",
  temporary_failure: "Resend could not check the records just now. It tries again on its own.",
  missing: "Not added to Resend yet.",
  unknown: "NOLA's Resend key can send email but cannot see domains. Add the domain in the Resend dashboard (Domains, Add domain) and put its DNS records in GoDaddy; emails switch to it on their own once Resend verifies it.",
};

/** The Resend sending domain behind notifications@nola.nolimits.marketing: its status and the DNS records to add. */
export function SendingDomain() {
  const status = useApi<EmailDomain>("/api/settings/email-domain");
  const [busy, setBusy] = useState<"" | "add" | "verify">("");
  const [error, setError] = useState("");
  const [data, setData] = useState<EmailDomain | null>(null);
  const d = data || status.data;
  async function run(kind: "add" | "verify") {
    setBusy(kind); setError("");
    try { setData(await api<EmailDomain>(`/api/settings/email-domain${kind === "verify" ? "/verify" : ""}`, { method: "POST", body: "{}" })); }
    catch (e) { setError(e instanceof Error ? e.message : "Resend did not answer."); }
    finally { setBusy(""); }
  }
  if (status.loading && !d) return null;
  if (!d) return <section className="integration-setup"><h3>Sending domain</h3><p role="alert" className="integration-notice danger">{status.error || "Resend did not answer."}</p></section>;
  const records = d.records || [];
  return <section className="integration-setup sending-domain">
    <h3>Sending domain</h3>
    <p>Emails come from <strong>{d.from_now}</strong>{d.from_now !== d.from_wanted ? `, and switch to ${d.from_wanted} once ${d.domain} is verified.` : "."}</p>
    <p className="integration-detail">{d.domain}: {DOMAIN_STATUS[d.status] || d.status}</p>
    {records.length > 0 && d.status !== "verified" && (
      <div className="table-wrap">
        <table className="sending-domain-records">
          <thead><tr><th>Type</th><th>Name</th><th>Value</th><th>Priority</th></tr></thead>
          <tbody>{records.map((r, i) => (
            <tr key={i}><td>{r.type}</td><td><code>{r.name}</code></td><td><code>{r.value}</code></td><td>{r.priority ?? ""}</td></tr>
          ))}</tbody>
        </table>
      </div>
    )}
    {error && <p role="alert" className="integration-notice danger">{error}</p>}
    {d.status === "unknown" ? null : d.status === "missing"
      ? <button className="button secondary" disabled={!!busy} onClick={() => void run("add")}>{busy === "add" ? "Adding…" : `Add ${d.domain} to Resend`}</button>
      : d.status !== "verified" && <button className="button secondary" disabled={!!busy} onClick={() => void run("verify")}>{busy === "verify" ? "Checking…" : "Check DNS now"}</button>}
  </section>;
}

type MyGoogleStatus = { connected: boolean; email: string | null; gmail: boolean; calendar: boolean };

/**
 * This person's own Google account: their Gmail becomes their work queue tasks, and
 * booking pages they host use their own calendar. Every team member has this card.
 */
export function MyGoogle() {
  const status = useApi<MyGoogleStatus>("/api/me/google");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const s = status.data;
  async function connect() {
    setBusy(true); setError("");
    try {
      const { auth_url } = await api<{ auth_url: string }>("/api/me/google/connect", { method: "POST", body: JSON.stringify({ gmail: true, calendar: true }) });
      const url = new URL(auth_url);
      if (url.origin !== "https://accounts.google.com") throw new Error("Unexpected Google sign-in address.");
      window.location.assign(url.href);
    } catch (e) { setError(e instanceof Error ? e.message : "Could not open Google."); setBusy(false); }
  }
  async function disconnect() {
    setBusy(true); setError("");
    try { await api("/api/me/google", { method: "DELETE" }); status.reload(); }
    catch (e) { setError(e instanceof Error ? e.message : "Could not disconnect."); }
    finally { setBusy(false); }
  }
  if (status.loading && !s) return null;
  const missing = s?.connected ? [!s.gmail && "Gmail", !s.calendar && "Calendar"].filter(Boolean) : [];
  return <section className="panel my-google" aria-labelledby="my-google-title">
    <h2 id="my-google-title">Your Gmail and Calendar</h2>
    <p>Emails that ask you for something become tasks in your work queue, and booking pages you host check your calendar and add the call to it. NOLA only reads your inbox: nothing is sent, deleted or marked read.</p>
    {s?.connected
      ? <p className="my-google-state">Connected{ s.email ? ` as ${s.email}` : "" }.{missing.length ? ` ${missing.join(" and ")} ${missing.length === 1 ? "was" : "were"} not allowed; connect again to allow ${missing.length === 1 ? "it" : "them"}.` : ""}</p>
      : null}
    {error && <p role="alert" className="integration-notice danger">{error}</p>}
    <div className="my-google-actions">
      <button className="button secondary" disabled={busy} onClick={() => void connect()}>{busy ? "Opening Google…" : s?.connected ? (missing.length ? "Connect again" : "Reconnect") : "Connect Google"}</button>
      {s?.connected && <button className="text-button" disabled={busy} onClick={() => void disconnect()}>Disconnect</button>}
    </div>
  </section>;
}

/** Whether the stored Google token was granted Search Console (Keywords page). */
function SearchConsoleNote() {
  const status = useApi<{ scope: boolean }>('/api/keywords/search-console/scope');
  if (!status.data) return null;
  return <p className="integration-detail">
    {status.data.scope
      ? 'Search Console is allowed. The Keywords page reads each client\'s searches with it.'
      : 'Search Console is not allowed yet. Add webmasters.readonly to the OAuth consent screen and enable the Search Console API in Google Cloud, then press Reconnect Google and allow it.'}
  </p>;
}

/** Optional: let the work queue read the connected account's inbox for tasks. */
function GmailConnect({ busy, onAllow }: { busy: boolean; onAllow: () => void }) {
  const status = useApi<IngestionStatus>('/api/ingestion/status');
  const gmail = status.data?.gmail;
  return <div className="gmail-connect">
    <h4>Tasks from Gmail</h4>
    <p>
      Lets NOLA read this Google account's inbox and turn emails that ask for something into work queue tasks.
      It only reads: nothing is sent, deleted or marked read. Newsletters and notifications are skipped.
    </p>
    {gmail?.authorised
      ? <p>Gmail is allowed{gmail.account_email ? ` for ${gmail.account_email}` : ''}. {gmail.owner === 'vercel'
          ? lastRun(gmail.last_checked, gmail.last_result, 'processed')
          : 'It starts once Gmail ingestion is set to This Vercel deployment in System & security, Scheduled job ownership.'}</p>
      : <>
          <p>Gmail read access is a restricted Google permission. Add <code>gmail.readonly</code> to the OAuth consent screen in Google Cloud first.</p>
          <button className="button secondary" disabled={busy} onClick={onAllow}>Allow Gmail</button>
        </>}
  </div>;
}

/** Optional: let the CRM's booking pages read free/busy and add booked calls to Google Calendar. */
function CalendarConnect({ busy, onAllow }: { busy: boolean; onAllow: () => void }) {
  const status = useApi<{ allowed: boolean; calendars?: { id: string; name: string; primary: boolean }[]; error?: string }>('/api/crm/google');
  if (!status.data) return null;
  const main = status.data.calendars?.find((c) => c.primary);
  return <div className="gmail-connect">
    <h4>Bookings on Google Calendar</h4>
    <p>
      Lets the CRM's booking pages skip times you are already busy, add each booked call to your calendar with a Google Meet link,
      and have Google email the invite. It only touches events it creates. A host who connected their own calendar
      (under Your Gmail and Calendar in Settings) gets their bookings on it instead.
    </p>
    {status.data.allowed
      ? <p>Calendar is allowed{main ? ` for ${main.id}` : ''}.{status.data.error ? ` ${status.data.error}` : ''}</p>
      : <>
          <p>Turn on the Google Calendar API in Google Cloud and add <code>calendar.events</code> and <code>calendar.readonly</code> to the OAuth consent screen first.</p>
          <button className="button secondary" disabled={busy} onClick={onAllow}>Allow Calendar</button>
        </>}
  </div>;
}

type GhlLocation = { id: number; name: string; ghl_location_id: string | null; has_token: boolean };
type GhlClient = { id: number; name: string; ghl_location_id: string | null; has_token: boolean; locations?: GhlLocation[] };
export function GhlSetup({ onChanged, vaultReady, clientId: fixedId }: { onChanged: () => void; vaultReady: boolean; clientId?: number }) {
  const status = useApi<{ clients: GhlClient[] }>('/api/ghl/status');
  const [clientId, setClientId] = useState(fixedId ? String(fixedId) : '');
  const [location, setLocation] = useState('');
  // '' = the client's own sub-account; otherwise the NOLA location this sub-account belongs to.
  const [place, setPlace] = useState('');
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const clients = status.data?.clients || [];
  const selected = clients.find(c => String(c.id) === clientId);
  const places = selected?.locations || [];
  const placeRow = places.find(l => String(l.id) === place);
  const linked = places.filter(l => l.ghl_location_id);
  // One client's page: fill in the location it already has once the list loads.
  useEffect(() => {
    if (fixedId && selected && !location) setLocation(selected.ghl_location_id || '');
  }, [fixedId, selected?.id]);
  async function save() {
    setBusy(true); setNotice(null);
    try {
      await api(`/api/ghl/token/${clientId}`, { method: 'PUT', body: JSON.stringify({ token: token.trim(), location_id: location.trim(), nola_location_id: place || null }) });
      const who = [selected?.name || 'This client', placeRow?.name].filter(Boolean).join(', ');
      setToken(''); setNotice({ ok: true, text: `${who}: token saved and contact read access verified. Reporting permissions are checked during sync.` });
      status.reload(); onChanged();
    } catch (e) { setNotice({ ok: false, text: e instanceof Error ? e.message : 'Token could not be verified.' }); }
    finally { setBusy(false); }
  }
  return <section className="integration-setup">
    {/* One client's page names the thing, like its neighbours (Windsor reporting, BrightLocal locations); the line below gives its state. */}
    <h3>{fixedId ? 'GoHighLevel sub-account' : 'Connect a client sub-account'}</h3>
    {fixedId && status.data && <p className="integration-detail">{selected?.has_token ? 'Token saved.' : 'No token yet.'}</p>}
    <p>Use a sub-account Private Integration token and its location ID. An agency token alone does not grant the contact and reporting access this connection needs.</p>
    <p>Enable read access for contacts, opportunities, calendars, and calendar events. When each location has its own sub-account, pick the location and save its token, so won revenue splits by location.</p>
    {status.error ? <ErrorState message={status.error} onRetry={status.reload} /> : !status.data && status.loading ? <LoadingState label="Loading clients" /> : <>
      <div className="credential-fields">
        {!fixedId && <label className="credential-field"><span>NOLA client</span><select value={clientId} disabled={busy} onChange={e => {
          setClientId(e.target.value); setPlace(''); setLocation(clients.find(c => String(c.id) === e.target.value)?.ghl_location_id || ''); setToken(''); setNotice(null);
        }}><option value="">Choose a client</option>{clients.map(c => <option key={c.id} value={c.id}>{c.name}{c.has_token ? ' · Token saved' : ''}</option>)}</select></label>}
        {places.length > 1 && <label className="credential-field"><span>Location</span><select value={place} disabled={busy} onChange={e => {
          const next = places.find(l => String(l.id) === e.target.value);
          setPlace(e.target.value); setLocation(next ? next.ghl_location_id || '' : selected?.ghl_location_id || ''); setToken(''); setNotice(null);
        }}><option value="">Whole client (one sub-account)</option>{places.map(l => <option key={l.id} value={l.id}>{l.name}{l.has_token ? ' · Token saved' : ''}</option>)}</select></label>}
        <label className="credential-field"><span>GHL location ID</span><input value={location} disabled={busy} onChange={e => setLocation(e.target.value)} placeholder="From the sub-account URL" autoComplete="off" /></label>
        <label className="credential-field"><span>Private Integration token</span><input type="password" value={token} disabled={busy} onChange={e => setToken(e.target.value)} placeholder={selected?.has_token ? 'Enter a replacement token' : 'Paste the sub-account token'} autoComplete="new-password" /></label>
      </div>
      {notice && <p role={notice.ok ? 'status' : 'alert'} className={`integration-notice ${notice.ok ? 'success' : 'danger'}`}>{notice.text}</p>}
      <button className="button secondary" disabled={busy || !vaultReady || !clientId || !location.trim() || !token.trim()} onClick={save}>{busy ? 'Verifying…' : placeRow ? `Verify & save ${placeRow.name} token` : 'Verify & save client token'}</button>
      {linked.length > 0 && <p className="integration-detail">Linked by location: {linked.map(l => `${l.name}${l.has_token ? '' : ' (no token)'}`).join(', ')}.</p>}
    </>}
  </section>;
}

const googleResults: Record<string, string> = {
  authorized: 'Google authorization saved. Use Test connection on Google Ads to verify manager-account access.',
  declined: 'Google authorization was cancelled. Your previous connection was kept.',
  invalid_session: 'The Google authorization session expired or could not be verified. Start Connect Google again.',
  missing_scopes: 'Google did not grant all required permissions. Reconnect and allow both Drive and Google Ads. Your previous connection was kept.',
  no_refresh_token: 'Google did not return offline access. Reconnect Google and grant consent again.',
  failed: 'Google authorization could not be saved. Check the OAuth application and redirect URL, then try again.',
  team_connected: 'Your Google account is connected.',
  team_declined: 'Google sign-in was cancelled. Nothing changed.',
  team_failed: 'Your Google account could not be connected. Try again, or ask an administrator to check the Google setup.',
};
export function GoogleReturnNotice() {
  const [result, setResult] = useState(() => new URLSearchParams(window.location.search).get('google'));
  if (!result || !googleResults[result]) return null;
  return <div className={`integration-notice ${result === 'authorized' || result === 'team_connected' ? 'success' : 'danger'}`} role="status">
    <span>{googleResults[result]}</span><button className="button secondary" onClick={() => { setResult(null); window.history.replaceState(null, '', window.location.pathname + window.location.hash); }}>Dismiss</button>
  </div>;
}
